/**
 * External dependencies
 */

/**
 * React dependencies
 */

import React, { useEffect, useRef, useState } from 'react';

/**
 * DWT dependencies
 */

import useFetchBasicRequest from '../../hooks/useFetchBasicRequest';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import useMediaQuery from '../../hooks/useMediaQuery';
import useViewportHeight from '../../hooks/useViewportHeight';
import { closeButtonIcon, filterIcon } from '../../img/icons';

/**
 * DWT components
 */

import Breadcrumbs from '../Breadcrumbs';
import Sort from '../Sort/Sort';
import FilterHeading from './FilterHeading';
import FilterNav from './FilterNav';
import FilterOptions from './FilterOptions';
import { FilterDataManager } from './FilterDataManager';

/**
 * Filter
 *
 * Filter component
 *
 * @param {Object} props
 * @param {string} props.context
 * @param {number} props.recordCount
 * @param {Object} props.setStart
 * @param {boolean} props.error
 */
const FilterContainer = ({ context, recordCount, setStart, error }) => {
  useViewportHeight();

  const pageUrlParts = window.location.pathname.split( '/' ).filter( Boolean );
  const postTypeMap = {
    'book-category': 'book-category',
    'imprint': 'imprint',
    'series': 'series',
    'age-range': 'age-range',
    'brand': 'brand',
    'book-theme': 'book-theme',
    'collection': 'collection'
  };
  const currentPostType = postTypeMap[pageUrlParts[0]] || 'books';

  const [ filterData, setFilterData ] = useState({});
  const [ navDataSource, setNavDataSource ] = useState( [] );
  const [ isOpen, setIsOpen ] = useState( false );
  const [ showBreadcrumb ] = useState( context?.showBreadcrumb === 'true' );

  const filterRef = useRef( null );
  const filterHeadingRef = useRef( null );
  const breadcrumbsRef = useRef( null );
  const footerRef = useRef( document.querySelector( '.site-footer' ) );
  const containerRef = useRef( document.querySelector( '.block__list-with-filters' ) );

  const isDesktop = useMediaQuery( '( min-width: 1024px )' );
  const doc = new DOMParser().parseFromString( window.DP.post_title, 'text/html' );
  const postTitle = doc.documentElement.textContent;

  const {
    makeRequest,
    data
  } = useFetchBasicRequest(
    [
      `wp-json/wp/v2/agerange?per_page=100`,
      `wp-json/wp/v2/bookcategory?per_page=100`,
      `wp-json/wp/v2/brand?per_page=100`,
      `wp-json/wp/v2/imprint?per_page=100`,
      `wp-json/wp/v2/series?per_page=100`,
      `wp-json/wp/v2/booktheme?per_page=100`,
      `wp-json/wp/v2/collection?per_page=100`,
    ],
    'GET'
  );

  useEffect( () => {
    if ( data.length === 0 ) return;

    const dataManager = new FilterDataManager( data );
    
    // Get current post ID from body classes
    const bodyClasses = document.body.classList;
    const postIDClass = [ ...bodyClasses ].find( item => item.startsWith( 'postid' ) );
    const postID = postIDClass?.split( '-' ).pop();

    const associatedData = dataManager.getAssociatedData( currentPostType, postID );
    setFilterData( associatedData );

    // Set navigation data
    const showNav = dataManager.shouldShowNavigation( currentPostType, associatedData );
    if ( showNav ) {
      setNavDataSource( [
        associatedData.ageRange,
        associatedData.brand,
        associatedData.category,
        associatedData.collection,
        associatedData.imprint,
        associatedData.series,
        associatedData.theme
      ] );
    } else {
      setNavDataSource( [] );
    }
  }, [ data, currentPostType ] );

  useEffect( () => {
    makeRequest();
  }, [] );

  const openMobleFilterMenu = () => setIsOpen( true );
  const closeMobileFilterMenu = () => setIsOpen( false );

  const noMenu = currentPostType === 'series' || currentPostType === 'collection';

  const resetAllSortFilter = () => {
    if ( noMenu ) return;

    const resetButtons = document.getElementsByClassName( 'filters__btn--reset' );
    for ( const button of resetButtons ) {
      button.click();
    }
  };

  const handleClickOffMainMenu = ( e ) => {
    if ( noMenu ) return;

    if ( filterRef && filterRef.current && !filterRef.current.contains( e.target ) ||
      filterRef.current.contains( e.target ) && e.target.classList.contains( 'filters__btn-close' ) ) {
      setIsOpen( false );
    }
  };

  useEffect( () => {
    document.body.classList.toggle( 'is-open-menu', isOpen );
    document.addEventListener( 'mousedown', handleClickOffMainMenu );
    return () => document.removeEventListener( 'mousedown', handleClickOffMainMenu );
  }, [ isOpen ] );

  const [ headingIsVisible, headingPosition ] = useIntersectionObserver( filterHeadingRef );
  const [ footerIsVisible ] = useIntersectionObserver( footerRef );
  const scrollToTop = () => window.requestAnimationFrame( () => containerRef.current.scrollIntoView() );

  return (
    <>
      {showBreadcrumb && (
        <Breadcrumbs ref={breadcrumbsRef} context={context} postTitle={postTitle} seriesPost={currentPostType === 'series'} />
      )}
      <FilterHeading context={context} postTitle={postTitle} recordCount={recordCount} error={error} ref={filterHeadingRef} />
      
      {!isDesktop && !noMenu && (
        <button
          className={`filters__btn--mobile capitalize py-15 col-span-full flex gap-15 items-center justify-center bg-black text-white rounded-[2px] mb-10 lg:hidden ${!headingPosition && !headingIsVisible ? 'hidden' : ''} ${!headingIsVisible ? 'fixed bottom-[40px] left-1/2 -translate-x-1/2 mb-0 z-40 w-[190px]' : ''} ${footerIsVisible ? 'hidden' : ''} ${breadcrumbsRef.current !== null ? 'row-start-3' : 'row-start-2'}`}
          type='button'
          onClick={openMobleFilterMenu}
        >
          <span className='filters-icon'>{filterIcon}</span>
          <span>Sort & Filter</span>
        </button>
      )}
      
      {isDesktop && currentPostType !== 'collection' && (
        <Sort context={context} setStart={setStart} scrollToTop={scrollToTop} />
      )}
      
      {!noMenu && (
        <div ref={filterRef} className={`list-with-filters__filters lg:col-start-1 lg:row-start-3 ${!isDesktop ? 'is-mobile shadow w-[90%] fixed bg-white top-0 right-0 overflow-y-scroll z-50 md:w-[337px]' : ''} ${isOpen && !isDesktop ? 'is-open' : ''}`} style={{ height: !isDesktop ? 'calc(var(--vh, 1vh) * 100)' : 'auto' }}>
          
          {!isDesktop && (
            <>
              <div className='filters__top-bar bg-[#121212] text-white flex gap-15 p-20 items-center justify-between'>
                <div className='wrap flex gap-15 items-center'>
                  <span className='filters__btn--icon'>{filterIcon}</span>
                  <span>Sort & Filter</span>
                </div>
                <button
                  className='filters__btn-close'
                  type='button'
                  onClick={closeMobileFilterMenu}
                >
                  {closeButtonIcon}
                </button>
              </div>
              <Sort context={context} setStart={setStart} scrollToTop={scrollToTop} />
            </>
          )}
          
          {navDataSource.length > 0 && <FilterNav data={navDataSource} />}
          
          <FilterOptions
            context={{ ...context, filterData }}
            currentPostType={currentPostType}
            setStart={setStart}
            scrollToTop={scrollToTop}
          />
          
          {!isDesktop && (
            <div className='sticky bottom-0 bg-inherit filters__bottom-bar flex gap-15 px-30 py-20 border-t border-[#121212]'>
              <button
                className='filters__btn-close bg-[#121212] text-white w-[50%] py-[15.5px]'
                type='button'
                onClick={closeMobileFilterMenu}
              >
                Done
              </button>
              <button
                className='text-[#121212] bg-inherit border border-[#121212] w-[50%] py-[15.5px]'
                type='button'
                onClick={resetAllSortFilter}
              >
                Reset All
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FilterContainer;
